package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * An application-defined synthetic fixture recipe, not the records themselves.
 * The application owns the fixture identified by [key]; Lingvo never executes or stores its data.
 */
@Serializable
data class ScreenshotDemoDataProfileDto(
    val key: String,
    val title: String,
    val seed: Int = 0,
    val recordCount: Int = 5,
)

/** Shared conservative admission rules keep recipe metadata bounded across REST, CLI and agent tools. */
object ScreenshotDemoDataProfiles {
    const val MAX_PROFILES: Int = 20
    const val MAX_RECORD_COUNT: Int = 1000
    private val keyPattern = Regex("^[a-z][a-z0-9_-]{0,47}$")
    private val credentialWords = Regex(
        "password|secret|credential|authorization|bearer|token|api[ _-]?key|private[ _-]?key",
        RegexOption.IGNORE_CASE,
    )

    /** Returns only safe fixed text: invalid submitted metadata must never appear in an error. */
    fun validate(profiles: List<ScreenshotDemoDataProfileDto>): String? {
        if (profiles.size > MAX_PROFILES) return "A plan holds at most 20 synthetic demo-data profiles."
        if (profiles.map { it.key }.distinct().size != profiles.size) return "Synthetic demo-data profile keys must be unique."
        for (profile in profiles) {
            if (!keyPattern.matches(profile.key)) return "A synthetic profile key is 1–48 lowercase letters, digits, '-' or '_', starting with a letter."
            if (profile.title.isBlank() || profile.title.length > 80 || profile.title != profile.title.trim() ||
                profile.title.any { !it.isLetterOrDigit() && it !in " _-.,'()" }
            ) return "A synthetic profile title is 1–80 letters, digits, spaces or simple label punctuation; no addresses, URLs or payloads."
            if (credentialWords.containsMatchIn(profile.key) || credentialWords.containsMatchIn(profile.title)) {
                return "Synthetic profile labels cannot contain credential fields."
            }
            if (profile.seed < 0) return "A synthetic profile seed is a nonnegative integer."
            if (profile.recordCount !in 0..MAX_RECORD_COUNT) return "A synthetic profile requests 0–1,000 sample records."
        }
        return null
    }
}

/** Enrich owed-capture instructions without rewriting the author's stored how-to or claiming fixture execution. */
fun demoDataProfileHowTo(howTo: String, profile: ScreenshotDemoDataProfileDto?): String {
    if (profile == null) return howTo
    val setup = "Use declared synthetic profile '${profile.title}' (${profile.key}), seed ${profile.seed}, " +
        "${profile.recordCount} sample records. Prepare this application fixture before capture; never use real user data or credentials."
    return if (howTo.isBlank()) setup else "$howTo\n\n$setup"
}
